//Component is self contained and manages state internally for impressions sub table data

import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { Avatar, Timeline, Row, Col, Tooltip, Card, Grid } from 'antd';
import { getMobileOperatingSystem, getReadableDomain, getFaviconUrl, getOutboundLink, isHtml5Creative, getDomainColor, isMeaningfulValue } from './utils';
import { formatCurrency2SigFig } from '../../../../../core/utils/campaigns';

import {
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  ArrowUpOutlined,
  CloudOutlined,
  Html5Outlined,
  ShoppingCartOutlined,
  ExportOutlined,
} from '@ant-design/icons';

const { Item } = Timeline;
const { useBreakpoint } = Grid;

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const touchBadgeStyle = {
  display: 'inline-block',
  fontSize: '10px',
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: '4px',
  marginLeft: '8px',
};

const ImpressionSubTable = ({ impressions, record, topImpressions, dsp }) => {
  const [impressionsData, setImpressionsData] = useState([]);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  useEffect(() => {
    const sortedImpressions =
      !isEmpty(impressions) &&
      impressions.sort((a, b) => {
        if (
          moment(a.IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).isBefore(new Date(b.IMPRESSION_TIME))
        )
          return -1;
        return 1;
      });

    setImpressionsData(sortedImpressions);
  }, [impressions]);

  const getUrlByName = (name) => {
    if (Array.isArray(topImpressions) && topImpressions.length > 0) {
      const entry = topImpressions.find((item) => item.unformatted === name);
      return entry ? entry.url : '';
    } else {
      return '';
    }
  };

  const getFriendlyName = (name) => {
    if (Array.isArray(topImpressions) && topImpressions.length > 0) {
      const entry = topImpressions.find((item) => item.unformatted === name);
      return entry ? entry.name : '';
    } else {
      return name;
    }
  };

  const renderSourceAvatar = (domain, inventoryType) => {
    const url = getUrlByName(domain) || getFaviconUrl(domain, inventoryType);
    if (url) {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          icon={<CloudOutlined />}
          src={url}
          style={{ marginRight: isMobile ? 0 : '16px', flexShrink: 0 }}
        />
      );
    }
    if (domain && domain.startsWith('In-App #')) {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          style={{
            marginRight: isMobile ? 0 : '16px',
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          #
        </Avatar>
      );
    }
    if (domain && domain !== 'Unknown Domain') {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          style={{
            marginRight: isMobile ? 0 : '16px',
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {domain.charAt(0).toUpperCase()}
        </Avatar>
      );
    }
    return (
      <Avatar
        shape="square"
        size={isMobile ? 40 : 50}
        icon={<CloudOutlined />}
        style={{ marginRight: isMobile ? 0 : '16px', flexShrink: 0 }}
      />
    );
  };

  const formatAttribution = (method) => {
    if (!method) return '';
    let parsed = method;
    if (typeof method === 'string') {
      try {
        parsed = JSON.parse(method);
      } catch (e) {
        return method;
      }
    }
    const key = Object.keys(parsed)[0];
    if (!key) return '';
    return key
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const makeTimeLine = impressionsData.map((impression, i) => {
    const {
      IMPRESSION_TIME,
      IMPRESSION_USERAGENT,
      IMPRESSION_PAGE_URL,
      CREATIVEURL,
    } = impression;
    const { impressionDomain, inventoryType } = getReadableDomain(impression, dsp);

    const APPSITENAME = impressionDomain;
    const APPLICATION_PLATFORM = inventoryType;
    const OS = getMobileOperatingSystem(IMPRESSION_USERAGENT);

    // Timeline is reversed, so array order is opposite of visual order
    const isFirstTouch = i === 0;
    const isLastTouch = i === impressionsData.length - 1;

    let icon = () => {
      if (
        OS === 'Unknown' ||
        OS === 'Chromebook Computer' ||
        OS === 'Windows Computer' ||
        OS === 'Computer'
      ) {
        return <DesktopOutlined />;
      }

      if (OS === 'iPhone' || OS === 'Android' || OS === 'Windows Phone') {
        return <MobileOutlined />;
      }

      if (OS === 'iPad') {
        return <TabletOutlined />;
      }
    };

    const touchBadge = () => {
      if (isFirstTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#e6f7ff', color: '#1890ff' }}>
            First Touch
          </span>
        );
      }
      if (isLastTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#f6ffed', color: '#52c41a' }}>
            Last Touch
          </span>
        );
      }
      return null;
    };

    if (isMobile) {
      return (
        <Item key={i} dot={<ArrowUpOutlined style={{ fontSize: 16 }} />}>
          <div style={{ padding: '8px 0', width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                fontFamily: 'roboto',
                fontSize: '105%',
                fontWeight: 700,
                color: '#636363',
              }}
            >
              <span>{`${moment(IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).format(
                'MM/DD/YY - HH:mm'
              )}`}</span>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Impression
                {touchBadge()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <Avatar
                shape="square"
                size={40}
                icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <CloudOutlined />}
                src={CREATIVEURL === '' || !CREATIVEURL ? false : CREATIVEURL}
                style={{
                  backgroundColor:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#e6f7ff' : undefined,
                  color:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#1890ff' : undefined,
                }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#636363' }}>
                  {icon()} {OS}
                </div>
                <div style={{ fontSize: '90%', color: '#636363' }}>{APPLICATION_PLATFORM}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderSourceAvatar(APPSITENAME, APPLICATION_PLATFORM)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={getFriendlyName(APPSITENAME)}>
                  <div style={{ ...ellipsisStyle, fontWeight: 600, color: '#636363' }}>
                    {getFriendlyName(APPSITENAME)}
                  </div>
                </Tooltip>
                {getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS) && (
                  <a
                    href={getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open impression source"
                    title="Open impression source"
                    style={{ fontSize: '90%' }}
                  >
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Item>
      );
    }

    return (
      <Item key={i} dot={<ArrowUpOutlined style={{ fontSize: 16 }} />}>
        <Row style={{ paddingTop: '5px', paddingBottom: '5px' }}>
          <Col span={1}></Col>
          <Col span={4}>
            <div
              style={{
                fontFamily: 'roboto',
                fontSize: '105%',
                fontWeight: 700,
                color: '#636363',
              }}
            >
              {`${moment(IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).format('MM/DD/YY - HH:mm')}`}
            </div>
          </Col>
          <Col span={3}>
            <div
              style={{
                fontFamily: 'roboto',
                fontSize: '105%',
                fontWeight: 700,
                color: '#636363',
              }}
            >
              Impression
              {touchBadge()}
            </div>
          </Col>
          <Col span={2}>
            <Avatar
              shape="square"
              size={50}
              icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <CloudOutlined />}
              src={CREATIVEURL === '' || !CREATIVEURL ? false : CREATIVEURL}
              style={{
                marginTop: '-16px',
                backgroundColor:
                  isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#e6f7ff' : undefined,
                color:
                  isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#1890ff' : undefined,
              }}
            />
          </Col>
          <Col span={4}>
            <div
              style={{
                fontFamily: 'roboto',
                fontSize: '105%',
                fontWeight: 700,
                color: '#636363',
              }}
            >
              {icon()}
              <Tooltip title={IMPRESSION_USERAGENT ? IMPRESSION_USERAGENT : ''}>
                {`  ${OS}`}
              </Tooltip>
            </div>
          </Col>
          <Col span={3}>{APPLICATION_PLATFORM}</Col>
          <Col span={7}>
            <div
              style={{
                fontFamily: 'roboto',
                fontSize: '105%',
                fontWeight: 700,
                color: '#636363',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                marginTop: '-16px',
              }}
            >
              {renderSourceAvatar(APPSITENAME, APPLICATION_PLATFORM)}
              <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 'calc(100% - 66px)' }}>
                <Tooltip title={getFriendlyName(APPSITENAME)}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getFriendlyName(APPSITENAME)}
                  </div>
                </Tooltip>
                {getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS) && (
                  <a
                    href={getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open impression source"
                    title="Open impression source"
                    style={{ fontSize: '90%' }}
                  >
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Item>
    );
  });

  return (
    <div style={{ marginRight: isMobile ? 0 : 100 }}>
      <Card
        title="CUSTOMER JOURNEY"
        bodyStyle={{ padding: '0' }}
        headStyle={{
          backgroundColor: '#FAFAFA',
          borderBottom: '1px solid #ddd',
        }}
      >
        {!isMobile && (
          <Row
            style={{
              fontWeight: 'bold',
              backgroundColor: '#FAFAFA',
              borderBottom: '1px solid #ddd',
              paddingLeft: '50px',
            }}
          >
            <Col style={{ padding: '10px' }} span={1}></Col>
            <Col style={{ padding: '10px' }} span={4}>
              Time
            </Col>
            <Col style={{ padding: '10px' }} span={3}>
              Event
            </Col>
            <Col style={{ padding: '10px' }} span={2}>
              Ad Unit
            </Col>
            <Col style={{ padding: '10px' }} span={4}>
              Device Type
            </Col>
            <Col style={{ padding: '10px' }} span={3}>
              Inventory
            </Col>
            <Col style={{ padding: '10px' }} span={7}>
              Impression Source
            </Col>
          </Row>
        )}
        <br />
        <Timeline mode={'left'} reverse={true} style={{ padding: '10px', paddingLeft: isMobile ? '20px' : '50px' }}>
          {makeTimeLine}
          <Item dot={<ShoppingCartOutlined style={{ fontSize: 16, color: '#52c41a' }} />}>
            <div
              style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px 16px',
                marginLeft: isMobile ? 0 : '50px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <ShoppingCartOutlined style={{ fontSize: 20, color: '#52c41a' }} />
              <div>
                <div
                  style={{
                    fontFamily: 'roboto',
                    fontSize: '105%',
                    fontWeight: 700,
                    color: '#389e0d',
                  }}
                >
                  Order Placed — {formatCurrency2SigFig(record.transactionTotal || 0)}
                </div>
                {isMeaningfulValue(record.transactionId) && (
                  <div style={{ fontSize: '90%', color: '#636363', fontWeight: 600 }}>
                    Order #{record.transactionId}
                    {isMeaningfulValue(record.transactionCity) && isMeaningfulValue(record.transactionState)
                      ? ` • ${record.transactionCity}, ${record.transactionState}`
                      : ''}
                  </div>
                )}
                <div style={{ fontSize: '90%', color: '#636363' }}>
                  {`${moment(record.transaction, moment.DATETIME_LOCAL_MS).format(
                    'MM/DD/YY - HH:mm'
                  )}`}
                  {isMeaningfulValue(record.totalImpressions) ? ` • ${record.totalImpressions} impressions` : ''}
                  {isMeaningfulValue(record.attributionMethod) ? ` • ${formatAttribution(record.attributionMethod)}` : ''}
                </div>
              </div>
            </div>
          </Item>
        </Timeline>
      </Card>
    </div>
  );
};

export default ImpressionSubTable;
